// src/components/ShopPublicPage.js
import React, { useEffect, useRef, useState } from 'react';
import SignCustomizationOverlay from './SignCustomizationOverlay';
import KeychainCustomizationOverlay from './KeychainCustomizationOverlay';
import CoasterCustomizationOverlay from './CoasterCustomizationOverlay';
import LightboxCustomizationOverlay from './LightboxCustomizationOverlay';
import DeskOrganizerCustomizationOverlay from './DeskOrganizerCustomizationOverlay';
import BookmarkCustomizationOverlay from './BookmarkCustomizationOverlay';
import CakeTopperCustomizationOverlay from './CakeTopperCustomizationOverlay';

const clamp = (val, min, max) => Math.min(max, Math.max(min, val));

function Spinner({ size = 16 }) {
  const px = typeof size === 'number' ? `${size}px` : size;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <span
        style={{
          width: px,
          height: px,
          borderRadius: '50%',
          border: '2px solid rgba(15, 23, 42, 0.25)',
          borderTopColor: 'rgba(15, 23, 42, 0.95)',
          animation: 'spin 0.8s linear infinite',
          display: 'inline-block',
        }}
      />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </span>
  );
}

async function sha256Hex(str) {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle && typeof TextEncoder !== 'undefined') {
      const enc = new TextEncoder().encode(str);
      const buf = await crypto.subtle.digest('SHA-256', enc);
      const arr = Array.from(new Uint8Array(buf));
      return arr.map((b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    // fall through
  }
  return sha256HexFallback(str);
}

// Minimal SHA-256 fallback (pure JS)
function sha256HexFallback(ascii) {
  function rightRotate(value, amount) {
    return (value >>> amount) | (value << (32 - amount));
  }

  const mathPow = Math.pow;
  const maxWord = mathPow(2, 32);
  let result = '';

  const words = [];
  const asciiBitLength = ascii.length * 8;

  let hash = sha256HexFallback.h || [];
  let k = sha256HexFallback.k || [];
  let primeCounter = k.length;

  const isComposite = {};
  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (!isComposite[candidate]) {
      for (let i = 0; i < 313; i += candidate) isComposite[i] = candidate;
      hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
      k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
    }
  }
  sha256HexFallback.h = hash;
  sha256HexFallback.k = k;

  ascii += '\x80';
  while (ascii.length % 64 - 56) ascii += '\x00';

  for (let i = 0; i < ascii.length; i++) {
    const j = ascii.charCodeAt(i);
    words[i >> 2] |= j << ((3 - i) % 4) * 8;
  }
  words[words.length] = (asciiBitLength / maxWord) | 0;
  words[words.length] = asciiBitLength;

  for (let j = 0; j < words.length; ) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash.slice(0);

    for (let i = 0; i < 64; i++) {
      const w15 = w[i - 15],
        w2 = w[i - 2];

      const a = hash[0],
        e = hash[4];
      const temp1 =
        (hash[7] +
          (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) +
          ((e & hash[5]) ^ (~e & hash[6])) +
          k[i] +
          (w[i] =
            i < 16
              ? w[i]
              : (w[i - 16] +
                  (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) +
                  w[i - 7] +
                  (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) |
                0)) |
        0;

      const temp2 =
        ((rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) +
          ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]))) |
        0;

      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
      hash.pop();
    }

    for (let i = 0; i < 8; i++) hash[i] = (hash[i] + oldHash[i]) | 0;
  }

  for (let i = 0; i < 8; i++) {
    for (let j = 3; j + 1; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += (b < 16 ? '0' : '') + b.toString(16);
    }
  }
  return result;
}

async function solvePow({ salt, difficulty, abortRef }) {
  const target = '0'.repeat(Math.max(0, Number(difficulty) || 0));
  let i = 0;

  while (true) {
    if (abortRef.current) throw new Error('aborted');

    const nonce = String(i);
    const digest = await sha256Hex(`${salt}:${nonce}`);
    if (digest.startsWith(target)) return nonce;

    i += 1;

    if (i % 250 === 0) {
      await new Promise((r) => setTimeout(r, 0));
    }
  }
}

export default function ShopPublicPage({ shopSlug }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [shopData, setShopData] = useState(null);

  const [bannerUrl, setBannerUrl] = useState(null);
  const [hasBanner, setHasBanner] = useState(false);
  const [profileUrl, setProfileUrl] = useState(null);

  // static positions (we don't allow dragging on public page)
  const [bgPos, setBgPos] = useState({ x: 50, y: 50 });
  const [profileBgPos, setProfileBgPos] = useState({ x: 50, y: 50 });

  // ───────────────────────────────
  // Anti-bot (PoW + Honeypot) — now triggered when user clicks a product
  // ───────────────────────────────
  const [antiBotBusy, setAntiBotBusy] = useState(false);
  const [antiBotPassed, setAntiBotPassed] = useState(false);
  const [antiBotError, setAntiBotError] = useState('');
  const [challenge, setChallenge] = useState(null); // { challengeId, salt, difficulty }
  const abortPowRef = useRef(false);

  // Honeypots
  // #1: gate-level honeypot (inside anti-bot overlay)
  const antiBotCompanyRef = useRef(null);
  // #2: email overlay honeypot (kept as a second layer)
  const overlayCompanyRef = useRef(null);

  // ───────────────────────────────
  // Email overlay state (for product selection)
  // ───────────────────────────────
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [overlayProductKey, setOverlayProductKey] = useState(null);
  const [overlayEmail, setOverlayEmail] = useState('');
  const [emailError, setEmailError] = useState(false);

  // ───────────────────────────────
  // Anti-bot overlay state (shown when user clicks a product)
  // ───────────────────────────────
  const [isAntiBotOverlayOpen, setIsAntiBotOverlayOpen] = useState(false);
  const [pendingProductKey, setPendingProductKey] = useState(null);

  // ───────────────────────────────
  // Customization overlays state
  // ───────────────────────────────
  const [showSignOverlay, setShowSignOverlay] = useState(false);
  const [showKeychainOverlay, setShowKeychainOverlay] = useState(false);
  const [showCoasterOverlay, setShowCoasterOverlay] = useState(false);
  const [showLightboxOverlay, setShowLightboxOverlay] = useState(false);
  const [showDeskOrganizerOverlay, setShowDeskOrganizerOverlay] = useState(false);
  const [showBookmarkOverlay, setShowBookmarkOverlay] = useState(false);
  const [showCakeTopperOverlay, setShowCakeTopperOverlay] = useState(false);

  const [customerEmail, setCustomerEmail] = useState('');

  const isValidEmail = (val) => {
    if (!val) return false;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(val);
  };

  const normalizeChallenge = (data) => {
    if (!data || typeof data !== 'object') return null;

    // accept either schema from backend
    const challengeId = data.challengeId || data.challenge_id || data.id || data.challenge;
    const salt = data.salt || data.pow_salt || data.seed;
    const difficulty =
      data.difficulty ??
      data.difficulty_hex_zeros ??
      data.difficultyHexZeros ??
      data.zeros ??
      data.leadingZeros;

    if (!challengeId || !salt) return null;

    return {
      challengeId: String(challengeId),
      salt: String(salt),
      difficulty: Number(difficulty ?? 4),
    };
  };

  const refreshChallenge = async () => {
    try {
      const res = await fetch('/anti-bot/challenge', { cache: 'no-store' });
      const data = await res.json().catch(() => null);
      if (!res.ok) return null;

      const norm = normalizeChallenge(data);
      if (!norm) return null;

      setChallenge(norm);
      return norm;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    abortPowRef.current = false;
    refreshChallenge();
    return () => {
      abortPowRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeEmailOverlay = () => {
    setIsOverlayOpen(false);
    setOverlayProductKey(null);
    setOverlayEmail('');
    setEmailError(false);
    if (overlayCompanyRef.current) overlayCompanyRef.current.value = '';
  };

  const openEmailOverlay = (productKey) => {
    setOverlayProductKey(productKey);
    setOverlayEmail('');
    setEmailError(false);
    setIsOverlayOpen(true);

    // close any existing overlays (start new flow cleanly)
    setShowSignOverlay(false);
    setShowKeychainOverlay(false);
    setShowCoasterOverlay(false);
    setShowLightboxOverlay(false);
    setShowDeskOrganizerOverlay(false);
    setShowBookmarkOverlay(false);
    setShowCakeTopperOverlay(false);

    if (overlayCompanyRef.current) overlayCompanyRef.current.value = '';
  };

  const handleOverlaySubmit = () => {
    // honeypot #2 (email overlay)
    const hp = (overlayCompanyRef.current?.value || '').trim();
    if (hp) {
      setEmailError(true);
      return;
    }

    if (!isValidEmail(overlayEmail)) {
      setEmailError(true);
      return;
    }

    setCustomerEmail(overlayEmail);

    if (overlayProductKey === 'sign') {
      setShowSignOverlay(true);
    } else if (overlayProductKey === 'keychain') {
      setShowKeychainOverlay(true);
    } else if (overlayProductKey === 'coaster') {
      setShowCoasterOverlay(true);
    } else if (overlayProductKey === 'lightbox') {
      setShowLightboxOverlay(true);
    } else if (overlayProductKey === 'desk_organizer') {
      setShowDeskOrganizerOverlay(true);
    } else if (overlayProductKey === 'bookmark') {
      setShowBookmarkOverlay(true);
    } else if (overlayProductKey === 'cake_topper') {
      setShowCakeTopperOverlay(true);
    }

    closeEmailOverlay();
  };

  const resetAntiBotOverlay = () => {
    abortPowRef.current = true;
    abortPowRef.current = false;

    setAntiBotBusy(false);
    setAntiBotError('');
    setIsAntiBotOverlayOpen(false);
    setPendingProductKey(null);

    if (antiBotCompanyRef.current) antiBotCompanyRef.current.value = '';
  };

  const runAntiBot = async () => {
    setAntiBotError('');
    setAntiBotBusy(true);

    try {
      // honeypot #1 (gate-level)
      const hp = (antiBotCompanyRef.current?.value || '').trim();
      if (hp) {
        setAntiBotBusy(false);
        setAntiBotError('Anti-bot check failed. Please refresh and try again.');
        return false;
      }

      const localChallenge = challenge || (await refreshChallenge());
      if (!localChallenge) throw new Error('challenge_unavailable');

      const nonce = await solvePow({
        salt: localChallenge.salt,
        difficulty: localChallenge.difficulty,
        abortRef: abortPowRef,
      });

      if (abortPowRef.current) throw new Error('aborted');

      const vRes = await fetch('/anti-bot/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // IMPORTANT: backend expects challengeId (camelCase).
        // Sending BOTH keys is safe and future-proofs if you ever alias it.
        body: JSON.stringify({
          challengeId: localChallenge.challengeId,
          challenge_id: localChallenge.challengeId,
          nonce,
        }),
      });

      const vData = await vRes.json().catch(() => ({}));
      if (!vRes.ok || !vData?.ok) throw new Error(vData?.detail || 'verify_failed');

      setAntiBotBusy(false);
      setAntiBotPassed(true);
      setAntiBotError('');
      return true;
    } catch (e) {
      console.error('Anti-bot failed:', e);
      setAntiBotBusy(false);
      setAntiBotPassed(false);
      setAntiBotError('Anti-bot check failed. Please try again.');
      await refreshChallenge();
      return false;
    }
  };

  const handleProductClick = (productKey) => {
    // If already passed once, go straight to email overlay
    if (antiBotPassed) {
      openEmailOverlay(productKey);
      return;
    }

    // Otherwise open anti-bot overlay, and remember which product was clicked
    setPendingProductKey(productKey);
    setAntiBotError('');
    setIsAntiBotOverlayOpen(true);

    if (antiBotCompanyRef.current) antiBotCompanyRef.current.value = '';
  };

  const handleAntiBotContinue = async () => {
    if (!pendingProductKey) return;

    const ok = await runAntiBot();
    if (!ok) return;

    // Close anti-bot overlay and open the email overlay for the product they originally clicked
    setIsAntiBotOverlayOpen(false);
    const pk = pendingProductKey;
    setPendingProductKey(null);

    openEmailOverlay(pk);
  };

  // ───────────────────────────────
  // 1) Fetch shop info from backend
  // ───────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function fetchShop() {
      setStatus('loading');
      setErrorMsg('');
      setShopData(null);
      setBannerUrl(null);
      setProfileUrl(null);
      setHasBanner(false);

      try {
        const resp = await fetch(`/public-shop/${encodeURIComponent(shopSlug)}`, {
          cache: 'no-store',
        });

        if (!resp.ok) {
          const detail = await resp.json().catch(() => ({}));
          throw new Error(detail.detail || 'Shop not found or inactive');
        }

        const data = await resp.json();

        if (cancelled) return;

        setShopData(data);
        setStatus('ok');

        const ownerUsername = data.owner_username || '';
        if (!ownerUsername) return;

        // ───────────────────────────────
        // 2) Try to detect banner/profile presence
        // ───────────────────────────────
        const bannerPath = `/outputs/${ownerUsername}/Shop/Banner.png`;
        const profilePath = `/outputs/${ownerUsername}/Shop/Profile.png`;

        try {
          const headBanner = await fetch(bannerPath, {
            method: 'HEAD',
            cache: 'no-store',
          });
          if (!cancelled && headBanner.ok) {
            setHasBanner(true);
            setBannerUrl(`${bannerPath}?t=${Date.now()}`);
          }
        } catch {
          if (!cancelled) {
            setHasBanner(false);
            setBannerUrl(null);
          }
        }

        try {
          const headProfile = await fetch(profilePath, {
            method: 'HEAD',
            cache: 'no-store',
          });
          if (!cancelled && headProfile.ok) {
            setProfileUrl(`${profilePath}?t=${Date.now()}`);
          }
        } catch {
          if (!cancelled) {
            setProfileUrl(null);
          }
        }

        // Offsets (if present)
        const bannerOffset = data.banner_offset || {};
        const profileOffset = data.profile_offset || {};

        const bdx = Number.isFinite(bannerOffset.delta_x) ? bannerOffset.delta_x : 0;
        const bdy = Number.isFinite(bannerOffset.delta_y) ? bannerOffset.delta_y : 0;
        const pdx = Number.isFinite(profileOffset.delta_x) ? profileOffset.delta_x : 0;
        const pdy = Number.isFinite(profileOffset.delta_y) ? profileOffset.delta_y : 0;

        const bannerX = clamp(50 - bdx / 5, 0, 100);
        const bannerY = clamp(50 - bdy / 5, 0, 100);
        const profileX = clamp(50 - pdx / 5, 0, 100);
        const profileY = clamp(50 - pdy / 5, 0, 100);

        if (!cancelled) {
          setBgPos({ x: bannerX, y: bannerY });
          setProfileBgPos({ x: profileX, y: profileY });
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setErrorMsg(err.message || 'Failed to load shop');
        }
      }
    }

    fetchShop();

    return () => {
      cancelled = true;
    };
  }, [shopSlug]);

  // ───────────────────────────────
  // Loading state
  // ───────────────────────────────
  if (status === 'loading') {
    return (
      <div
        style={{
          width: '100%',
          minHeight: '100vh',
          padding: '20px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          backgroundColor: '#f3f4f6',
        }}
      >
        <div
          style={{
            borderRadius: '10px',
            backgroundColor: '#ffffff',
            padding: '24px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
            maxWidth: '800px',
            width: '100%',
          }}
        >
          <div style={{ fontSize: '3vh', fontWeight: 700, marginBottom: '8px' }}>
            Loading shop…
          </div>
          <div style={{ fontSize: '2vh', color: '#4b5563' }}>
            (slug: <code>{shopSlug}</code>)
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────
  // Error / inactive / not found
  // ───────────────────────────────
  if (status === 'error' || !shopData) {
    return (
      <div
        style={{
          width: '100%',
          minHeight: '100vh',
          padding: '20px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          backgroundColor: '#f3f4f6',
        }}
      >
        <div
          style={{
            borderRadius: '10px',
            backgroundColor: '#ffffff',
            padding: '24px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
            maxWidth: '800px',
            width: '100%',
          }}
        >
          <div style={{ fontSize: '3vh', fontWeight: 700, marginBottom: '8px' }}>
            Whoops! You&apos;re not in the right place...
          </div>
          <div style={{ fontSize: '2vh', color: '#4b5563', marginBottom: '8px' }}>
            {errorMsg || 'This shop is inactive or does not exist.'}
          </div>
          <div style={{ fontSize: '1.8vh', color: '#6b7280' }}>Please try again.</div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────
  // Success – read-only clone of MyStorefront layout
  // ───────────────────────────────
  const {
    shop_name,
    owner_username,
    shipping_from,
    contact_email,
    offered_products,
    selling_platforms,
    social_media,
    description,
  } = shopData;

  const displayDescription =
    typeof description === 'string' && description.trim().length > 0 ? description : '';

  const displayShopName = shop_name || shopSlug;
  const displayShippingFrom = shipping_from || '';
  const displayContactEmail = contact_email || '';

  // include all products (old + new)
  const offeredProductsNormalized = {
    sign: !!(offered_products && offered_products.sign),
    keychain: !!(offered_products && offered_products.keychain),
    coaster: !!(offered_products && offered_products.coaster),
    lightbox: !!(offered_products && offered_products.lightbox),
    desk_organizer: !!(offered_products && offered_products.desk_organizer),
    bookmark: !!(offered_products && offered_products.bookmark),
    cake_topper: !!(offered_products && offered_products.cake_topper),
  };

  // Selling platforms (Shopify / Etsy / eBay / Other)
  const platformLinks = {
    shopify: (selling_platforms && selling_platforms.shopify) || '',
    etsy: (selling_platforms && selling_platforms.etsy) || '',
    ebay: (selling_platforms && selling_platforms.ebay) || '',
    other: (selling_platforms && selling_platforms.other) || '',
  };

  // Social media links (Instagram / TikTok / X / Facebook / Pinterest / YouTube)
  const socialLinks = {
    instagram: (social_media && social_media.instagram) || '',
    tiktok: (social_media && social_media.tiktok) || '',
    x: (social_media && social_media.x) || '',
    facebook: (social_media && social_media.facebook) || '',
    pinterest: (social_media && social_media.pinterest) || '',
    youtube: (social_media && social_media.youtube) || '',
  };

  // labels for all products
  const productLabels = {
    sign: 'Sign',
    keychain: 'Keychain',
    coaster: 'Coaster',
    lightbox: 'Lightbox',
    desk_organizer: 'Desk Organizer',
    bookmark: 'Bookmark',
    cake_topper: 'Cake Topper',
  };

  const activeProducts = Object.entries(offeredProductsNormalized).filter(([, value]) => value);

  const platformDefs = [
    { key: 'shopify', label: 'Shopify', icon: '/icons/shopify.svg' },
    { key: 'etsy', label: 'Etsy', icon: '/icons/etsy.svg' },
    { key: 'ebay', label: 'eBay', icon: '/icons/ebay.svg' },
    { key: 'other', label: 'Other', icon: '/icons/other.svg' },
  ];

  const activePlatforms = platformDefs.filter((p) => platformLinks[p.key]);

  const socialDefs = [
    { key: 'instagram', label: 'Instagram', icon: '/icons/Ig.svg' },
    { key: 'tiktok', label: 'TikTok', icon: '/icons/TikTok.svg' },
    { key: 'x', label: 'X', icon: '/icons/X.svg' },
    { key: 'facebook', label: 'Facebook', icon: '/icons/Facebook.svg' },
    { key: 'pinterest', label: 'Pinterest', icon: '/icons/Pinterest.svg' },
    { key: 'youtube', label: 'YouTube', icon: '/icons/Youtube.svg' },
  ];

  const activeSocials = socialDefs.filter((s) => socialLinks[s.key]);

  const effectiveBannerUrl = bannerUrl || '/profileBanner.png';

  const currentProductLabel =
    overlayProductKey && productLabels[overlayProductKey]
      ? productLabels[overlayProductKey]
      : 'your product';

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        padding: '20px',
        backgroundColor: '#f3f4f6',
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      {/* --- Header Row (contact email only) --- */}
      {displayContactEmail && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            marginBottom: '20px',
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <span style={{ fontSize: '2.1vh', fontWeight: 600 }}>Contact Email:</span>
          <span style={{ fontSize: '2.1vh' }}>{displayContactEmail}</span>
        </div>
      )}

      {/* --- Bounding Box --- */}
      <div
        style={{
          border: '2px solid #ddd',
          borderRadius: '10px',
          padding: '20px',
          minHeight: '60vh',
          position: 'relative',
          backgroundColor: '#fff',
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Banner Display Box (read-only) */}
        <div
          style={{
            width: '100%',
            aspectRatio: '4 / 1',
            border: effectiveBannerUrl ? '2px solid #bbb' : '2px dashed #bbb',
            borderRadius: '12px',
            margin: '0 auto 8vmin auto',
            overflow: 'visible',
            backgroundColor: '#fafafa',
            backgroundImage: effectiveBannerUrl ? `url(${effectiveBannerUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: effectiveBannerUrl ? `${bgPos.x}% ${bgPos.y}%` : 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '1.8vh',
            userSelect: 'none',
            position: 'relative',
          }}
        >
          {/* Profile (read-only) */}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translate(-50%, -70%)',
              width: '25vmin',
              aspectRatio: '1 / 1',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                border: profileUrl ? '2px solid #bbb' : '2px dashed #bbb',
                borderRadius: '50%',
                overflow: 'hidden',
                backgroundColor: '#fafafa',
                backgroundImage: profileUrl ? `url(${profileUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: profileUrl ? `${profileBgPos.x}% ${profileBgPos.y}%` : 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: '1.6vh',
                userSelect: 'none',
                position: 'relative',
                boxShadow: '0 0 10px rgba(0,0,0,0.3)',
              }}
            >
              {!profileUrl && <span>Profile image not set</span>}
            </div>
          </div>
        </div>

        {/* Shop name */}
        <div
          style={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: '5vh',
            marginTop: '4px',
          }}
        >
          {displayShopName}
        </div>

        {/* Shipping from */}
        {displayShippingFrom && (
          <div
            style={{
              marginTop: '8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: '2.1vh' }}>
              <span style={{ fontWeight: 600 }}>Shipping from:</span>
            </span>
            <span style={{ fontSize: '2vh' }}>{displayShippingFrom}</span>
          </div>
        )}

        {/* Description */}
        {displayDescription && (
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
            <div
              style={{
                backgroundColor: '#f3f4f6',
                padding: '12px 16px',
                borderRadius: '8px',
                maxWidth: '900px',
                textAlign: 'center',
                fontSize: '2vh',
                whiteSpace: 'pre-line',
                color: '#374151',
              }}
            >
              {displayDescription}
            </div>
          </div>
        )}

        {/* Featured Products (buttons) */}
        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <div style={{ fontWeight: 600, fontSize: '2.4vh', marginBottom: '8px' }}>
            Featured Products:
          </div>

          {activeProducts.length > 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                marginTop: '4px',
              }}
            >
              {activeProducts.map(([key]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleProductClick(key)}
                  style={{
                    width: '70%',
                    maxWidth: '280px',
                    padding: '10px 18px',
                    fontSize: '2.1vh',
                    fontWeight: 700,
                    borderRadius: '999px',
                    border: '1px solid #10b981',
                    backgroundColor: '#22c55e',
                    color: '#022c22',
                    cursor: 'pointer',
                    boxShadow: '0 4px 10px rgba(34, 197, 94, 0.25)',
                    opacity: 1,
                  }}
                >
                  {productLabels[key] || key}
                </button>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '1.9vh', color: '#9ca3af', marginTop: '4px' }}>
              No products listed yet.
            </div>
          )}
        </div>

        {/* Selling Platforms */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <div style={{ fontWeight: 600, fontSize: '2.4vh', marginBottom: '10px' }}>
            Selling Platforms:
          </div>

          {activePlatforms.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
              {activePlatforms.map((p) => {
                const url = platformLinks[p.key];

                let linkLabel = p.label;
                if (p.key === 'other' && url) {
                  try {
                    const u = new URL(url);
                    const hostCore = u.hostname.replace(/^www\./i, '').split('.')[0];
                    if (hostCore) linkLabel = hostCore;
                  } catch {
                    const match = url.match(/^https?:\/\/(?:www\.)?([^./]+)\.[^/]+/i);
                    if (match && match[1]) linkLabel = match[1];
                  }
                }

                return (
                  <div
                    key={p.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      width: '100%',
                      maxWidth: '600px',
                      justifyContent: 'center',
                      margin: '0 auto',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '36px',
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={p.icon}
                        alt={p.label}
                        style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                      />
                    </div>

                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '1.9vh',
                        fontWeight: 700,
                        color: '#1d4ed8',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                      }}
                      title={url}
                    >
                      {linkLabel}
                    </a>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: '1.9vh', color: '#9ca3af', marginTop: '4px' }}>
              No selling platforms listed yet.
            </div>
          )}
        </div>

        {/* Social Media */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <div style={{ fontWeight: 600, fontSize: '2.4vh', marginBottom: '10px' }}>
            Social Media:
          </div>

          {activeSocials.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
              {activeSocials.map((s) => {
                const url = socialLinks[s.key];
                return (
                  <div
                    key={s.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      width: '100%',
                      maxWidth: '600px',
                      justifyContent: 'center',
                      margin: '0 auto',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '36px',
                        height: '36px',
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={s.icon}
                        alt={s.label}
                        style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                      />
                    </div>

                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '1.9vh',
                        fontWeight: 700,
                        color: '#1d4ed8',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                      }}
                      title={url}
                    >
                      {s.label}
                    </a>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: '1.9vh', color: '#9ca3af', marginTop: '4px' }}>
              No social media links listed yet.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ marginTop: '24px', fontSize: '1.6vh', color: '#9ca3af', textAlign: 'center' }}>
          Storefront managed by @{owner_username}
        </div>
      </div>

      {/* ───────────────────────────────
          Anti-bot Overlay (shown on first product click, before email overlay)
         ─────────────────────────────── */}
      {isAntiBotOverlayOpen && !antiBotPassed && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '10px',
              padding: '18px 20px 16px 20px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              minWidth: '260px',
              maxWidth: '360px',
              position: 'relative',
              textAlign: 'center',
            }}
          >
            <button
              type="button"
              onClick={resetAntiBotOverlay}
              style={{
                position: 'absolute',
                top: '8px',
                right: '10px',
                border: 'none',
                background: 'transparent',
                fontSize: '18px',
                cursor: 'pointer',
              }}
              aria-label="Close"
            >
              ×
            </button>

            <div style={{ fontSize: '2.0vh', fontWeight: 800, marginBottom: 8, color: '#0f172a' }}>
              Quick Anti-bot Check
            </div>

            {/* Gate-level honeypot (hidden) */}
            <input
              ref={antiBotCompanyRef}
              type="text"
              name="company"
              defaultValue=""
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-10000px',
                top: 'auto',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
              }}
            />

            <div style={{ fontSize: '1.6vh', color: '#475569', marginBottom: 12 }}>
              Please complete a short verification to continue.
            </div>

            <button
              type="button"
              onClick={handleAntiBotContinue}
              disabled={antiBotBusy}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '1.85vh',
                fontWeight: 800,
                borderRadius: '999px',
                border: '1px solid #0ea5e9',
                backgroundColor: '#38bdf8',
                color: '#082f49',
                cursor: antiBotBusy ? 'default' : 'pointer',
                opacity: antiBotBusy ? 0.85 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              {antiBotBusy ? (
                <>
                  <span>Verifying…</span>
                  <Spinner size={16} />
                </>
              ) : (
                'Continue'
              )}
            </button>

            {antiBotError && (
              <div style={{ marginTop: 10, fontSize: '1.55vh', color: '#ef4444', fontWeight: 800 }}>
                {antiBotError}
              </div>
            )}

            <div style={{ marginTop: 10, fontSize: '1.45vh', color: '#64748b' }}>
              This helps protect storefront owners from automated abuse.
            </div>
          </div>
        </div>
      )}

      {/* Email Overlay */}
      {isOverlayOpen && antiBotPassed && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '10px',
              padding: '18px 20px 16px 20px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              minWidth: '260px',
              maxWidth: '320px',
              position: 'relative',
            }}
          >
            <button
              type="button"
              onClick={closeEmailOverlay}
              style={{
                position: 'absolute',
                top: '8px',
                right: '10px',
                border: 'none',
                background: 'transparent',
                fontSize: '18px',
                cursor: 'pointer',
              }}
              aria-label="Close"
            >
              ×
            </button>

            <div style={{ fontSize: '2.1vh', fontWeight: 700, marginBottom: '6px' }}>
              Please enter your email:
            </div>
            <div style={{ fontSize: '1.7vh', color: '#4b5563', marginBottom: '10px' }}>
              We&apos;ll use it to start customizing {currentProductLabel}.
            </div>

            {/* Email-overlay honeypot (hidden second layer) */}
            <input
              ref={overlayCompanyRef}
              type="text"
              name="company"
              defaultValue=""
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-10000px',
                top: 'auto',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
              }}
            />

            <input
              type="email"
              value={overlayEmail}
              onChange={(e) => {
                setOverlayEmail(e.target.value);
                if (emailError) setEmailError(false);
              }}
              placeholder="you@example.com"
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: '6px',
                border: emailError ? '2px solid #ef4444' : '1px solid #d1d5db',
                fontSize: '1.7vh',
                marginBottom: emailError ? '4px' : '10px',
                boxSizing: 'border-box',
              }}
            />

            {emailError && (
              <div style={{ fontSize: '1.5vh', color: '#ef4444', marginBottom: '6px' }}>
                Please enter a valid email address.
              </div>
            )}

            <button
              type="button"
              onClick={handleOverlaySubmit}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '1.8vh',
                fontWeight: 700,
                borderRadius: '999px',
                border: '1px solid #10b981',
                backgroundColor: '#22c55e',
                color: '#022c22',
                cursor: 'pointer',
                marginTop: '4px',
              }}
            >
              Let&apos;s begin customizing!
            </button>
          </div>
        </div>
      )}

      {/* Product Overlays */}
      {showSignOverlay && (
        <SignCustomizationOverlay
          ownerEmail={displayContactEmail}
          ownerUsername={owner_username}
          customerEmail={customerEmail}
          onClose={() => setShowSignOverlay(false)}
        />
      )}

      {showKeychainOverlay && (
        <KeychainCustomizationOverlay
          ownerEmail={displayContactEmail}
          ownerUsername={owner_username}
          customerEmail={customerEmail}
          onClose={() => setShowKeychainOverlay(false)}
        />
      )}

      {showCoasterOverlay && (
        <CoasterCustomizationOverlay
          ownerEmail={displayContactEmail}
          ownerUsername={owner_username}
          customerEmail={customerEmail}
          onClose={() => setShowCoasterOverlay(false)}
        />
      )}

      {showLightboxOverlay && (
        <LightboxCustomizationOverlay
          ownerEmail={displayContactEmail}
          ownerUsername={owner_username}
          customerEmail={customerEmail}
          onClose={() => setShowLightboxOverlay(false)}
        />
      )}

      {showDeskOrganizerOverlay && (
        <DeskOrganizerCustomizationOverlay
          ownerEmail={displayContactEmail}
          ownerUsername={owner_username}
          customerEmail={customerEmail}
          onClose={() => setShowDeskOrganizerOverlay(false)}
        />
      )}

      {showBookmarkOverlay && (
        <BookmarkCustomizationOverlay
          ownerEmail={displayContactEmail}
          ownerUsername={owner_username}
          customerEmail={customerEmail}
          onClose={() => setShowBookmarkOverlay(false)}
        />
      )}

      {showCakeTopperOverlay && (
        <CakeTopperCustomizationOverlay
          ownerEmail={displayContactEmail}
          ownerUsername={owner_username}
          customerEmail={customerEmail}
          onClose={() => setShowCakeTopperOverlay(false)}
        />
      )}
    </div>
  );
}
